/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {
    ForwardedRef,
    forwardRef,
    MouseEventHandler,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import {discountOnCancel} from "../../../../util/requestSender";
import Subscription from "../../../../models/subscriptionModel";
import {AppContext} from "../../../misc/appContext";
import {onShowConfirmSnackbar} from "../../../../util/renderUtil";
import {createErrorMessage} from "../../../../util/utils";
import {ActivateHandle} from "../../../../util/interfaces";
import {onPopupClosed, requestOpenPopup} from "../../../../util/popupUtil";
import CancelDiscountOffer from "./cancelDiscountOffer";

interface Props {
    onProceed: MouseEventHandler;
    onSuccess: (subscription: Subscription) => void;
}

function CancelDiscountOfferController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {onProceed, onSuccess} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const popupId = useRef<string | null>(null);

    function onActivate() {
        popupId.current = requestOpenPopup(() => {
            setActive(true);
        }, true);
    }

    function onClose() {
        setActive(false);
        if (popupId.current) {
            onPopupClosed(popupId.current);
            popupId.current = null;
        }
    }

    function _onProceed(evt: React.MouseEvent) {
        onClose();
        onProceed(evt);
    }

    function onApplyDiscount() {
        if(!account) return;
        setLoading(true);
        discountOnCancel({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(true);
                if (data.success) {
                    setLoading(false);
                    const {user} = context.userContext;
                    if (user) {
                        user.subscription = new Subscription(data.subscription);
                        context.onChangeContext({user});
                        onSuccess(user.subscription);
                    }
                    onShowConfirmSnackbar('De korting is toegepast op jouw abonnement');
                    onClose();
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het toepassen van de korting is niet gelukt.'),
                        'Korting toepassen mislukt');
            }
        })
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    useEffect(() => {
        if (popupId.current) onPopupClosed(popupId.current);
    }, []);

    return (
        <CancelDiscountOffer onClose={onClose} onProceed={_onProceed} onApplyDiscount={onApplyDiscount} active={active}
                             loading={loading}/>
    );
}

export default forwardRef<ActivateHandle, Props>(CancelDiscountOfferController);